import React, { useCallback, useEffect, useState } from "react";
import { TimelineComponent } from "./Timeline.component";
import { useParams } from "react-router-dom";
import * as HomeApi from "api/student/v2/home.api";
import PostWritingPopupContainer from "../popup/PostWritingPopup.container";
import { usePopup } from "contexts/PopupContext";
import { IBbs } from "api/interfaces/bbs.interface";

export const TimelineContainer = (props: any) => {
    const { info, subject } = props;
    const [posts, setPosts] = useState<IBbs[]>([]);
    const [isLoadingPosts, setIsLoadingPosts] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const { showPopup } = usePopup();

    // 초기 데이터 로드 함수
    const fetchPosts = useCallback(async () => {
        if (isLoadingPosts) return;

        try {
            setIsLoadingPosts(true);
            setPage(1);
            setHasMore(true);

            const data = await HomeApi.fetchTimelineList(1, subject);
            if (data?.list) {
                setPosts(data.list);
                setHasMore(data.pagination.hasNextPage);
            }
        } catch (error) {
            console.error('Failed to fetch posts:', error);
            setPosts([]);
            setHasMore(false);
        } finally {
            setIsLoadingPosts(false);
        }
    }, [subject]);

    // 추가 데이터 로드 함수 (무한 스크롤)
    const loadMoreData = useCallback(async () => {
        // 중복 호출 방지: 이미 로딩 중이거나 더 이상 데이터가 없으면 즉시 반환
        if (isLoadingMore || !hasMore) {
            // console.log("🚫 loadMoreData 호출 차단:", { isLoadingMore, hasMore });
            return;
        }

        try {
            // console.log("🔄 추가 데이터 로드 시작:", { page, hasMore, isLoadingMore });
            setIsLoadingMore(true);
            const nextPage = page + 1;

            // 다음 페이지 데이터 가져오기
            const nextPageResponse = await HomeApi.fetchTimelineList(nextPage, subject);
            // console.log("📄 다음 페이지 응답:", { nextPage, dataLength: nextPageResponse?.list?.length });

            // 새 데이터가 있는지 확인
            if (!nextPageResponse?.list || nextPageResponse.list.length === 0) {
                // console.log("❌ 더 이상 데이터가 없음");
                setHasMore(false);
                return;
            }

            // 기존 데이터와 새 데이터 합치기
            setPosts(prevPosts => {
                const newPosts = [...prevPosts, ...nextPageResponse.list];
                console.log("📊 데이터 병합 완료:", {
                    기존개수: prevPosts.length,
                    새개수: nextPageResponse.list.length,
                    총개수: newPosts.length
                });
                return newPosts;
            });

            // 페이지 상태 업데이트
            setPage(nextPage);
            setHasMore(nextPageResponse.pagination.hasNextPage);
            // console.log("✅ 페이지 상태 업데이트:", { nextPage, hasNextPage: nextPageResponse.pagination.hasNextPage });
        } catch (error) {
            console.error("❌ 추가 데이터 로드 실패:", error);
            // 에러 발생 시 더 이상 로드하지 않도록 설정
            setHasMore(false);
        } finally {
            setIsLoadingMore(false);
        }
    }, [page, hasMore, isLoadingMore, subject]);

    // 스크롤 이벤트 감지 (무한 스크롤)
    useEffect(() => {
        let isScrolling = false; // 중복 스크롤 처리 방지 플래그
        let scrollTimeout: NodeJS.Timeout;

        const handleScroll = () => {
            // 이미 스크롤 처리 중이거나 로딩 중이거나 더 이상 데이터가 없으면 스킵
            if (isScrolling || isLoadingMore || !hasMore) {
                // console.log("🚫 스크롤 감지 스킵:", { isScrolling, isLoadingMore, hasMore });
                return;
            }

            // 스크롤이 페이지 하단에 도달했는지 확인
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollPosition = scrollTop + windowHeight;
            const triggerPosition = documentHeight - 200;

            // console.log("📏 스크롤 위치:", {
            //     scrollTop,
            //     windowHeight,
            //     documentHeight,
            //     scrollPosition,
            //     triggerPosition,
            //     shouldLoad: scrollPosition >= triggerPosition
            // });

            // 스크롤이 하단 200px 이내에 도달하면 추가 데이터 로드
            if (scrollPosition >= triggerPosition) {
                // console.log("🚀 무한 스크롤 트리거!");

                // 스크롤 처리 중 플래그 설정
                isScrolling = true;

                // loadMoreData 호출
                loadMoreData().finally(() => {
                    // API 호출 완료 후 플래그 해제 (1초 후)
                    clearTimeout(scrollTimeout);
                    scrollTimeout = setTimeout(() => {
                        isScrolling = false;
                        // console.log("✅ 스크롤 처리 완료, 플래그 해제");
                    }, 1000);
                });
            }
        };

        // 스크롤 이벤트 리스너 추가 (throttle 적용)
        let ticking = false;
        const throttledHandleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', throttledHandleScroll);

        // 컴포넌트 언마운트 시 이벤트 리스너 제거
        return () => {
            window.removeEventListener('scroll', throttledHandleScroll);
            clearTimeout(scrollTimeout);
        };
    }, [isLoadingMore, hasMore, loadMoreData]);

    useEffect(() => {
        console.log('subject', subject);
        fetchPosts();
    }, [fetchPosts]);

    const addPost = (post: any) => {
        setPosts(prev => [post, ...prev]);
    }

    const handlePostWritingPopup = useCallback(() => {
        showPopup(<PostWritingPopupContainer
            myPostCount={info?.myPostCount || 1}
            addPost={addPost}
            initSubject={subject}
        />);
    }, [info?.myPostCount, subject, showPopup]);

    return <TimelineComponent
        title={subject}
        posts={posts}
        setPosts={setPosts}
        isLoadingPosts={isLoadingPosts}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        handleLoadMore={loadMoreData}
        handlePostWritingPopup={handlePostWritingPopup}
    />;
};

export default TimelineContainer;   